'use client';

import React, { useMemo, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faList } from '@fortawesome/pro-solid-svg-icons';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import {
  Card,
  Checkbox,
  PulseButton,
} from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { AddToListModal } from 'src/components';
import { useGetReferralsV2PaginatedInfiniteQuery } from 'src/api/redux/referrals/referralsApi';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useInfiniteScroll } from 'src/hooks/common/useInfiniteScroll';
import { getSearchQueryParamsForAPI } from 'src/utils/url.helpers';
import { getProfileUrl } from 'src/utils/profileUrl';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';

import { applyCaptureRate } from 'src/components/Profiles/shared/widgets/utils';
import CaptureRateBadge from 'src/components/Profiles/shared/widgets/CaptureRateBadge';
import CompactCRMButton from './CompactCRMButton';

interface CompactReferralsListProps {
  direction: 'inbound' | 'outbound';
  title: string;
  testIdPrefix: string;
}

const PAGE_SIZE = 25;

const CompactReferralsList: React.FC<CompactReferralsListProps> = ({
  direction,
  title,
  testIdPrefix,
}) => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();
  const router = useRouter();
  const { closeDrawer } = useDrawer();
  const { providerId } = getEntityIdentifiers(entityDetails);

  const captureRate = widgetConfig?.widget?.capture_rate;
  const volumeFilterSetId = widgetConfig?.filterSet?.id;
  const volumeKey = volumeFilterSetId ? String(volumeFilterSetId) : null;
  const widgetFilterSetId = widgetConfig?.filterSetId;

  const [selectedProviders, setSelectedProviders] = useState<Set<string>>(
    new Set()
  );
  const [showAddToListModal, setShowAddToListModal] = useState(false);

  const queryParams = useMemo(() => {
    const params: Record<string, any> = {
      direction,
      page: 1,
      pageSize: PAGE_SIZE,
    };

    if (volumeKey) {
      params.sort = 'volume';
      params.sort_id = volumeKey;
      params.sort_field = 'patients';
      params.order = 'desc';
    } else {
      params.sort = 'total_patients';
      params.order = 'desc';
    }

    let qs = getSearchQueryParamsForAPI(params);

    if (widgetFilterSetId) {
      qs += `&${encodeURIComponent('filter_set')}=${encodeURIComponent(
        widgetFilterSetId
      )}`;
    }

    return qs;
  }, [direction, volumeKey, widgetFilterSetId]);

  const postBody = useMemo(() => {
    if (!volumeKey) return {};

    return {
      [volumeKey]: {
        code_set: widgetConfig?.widget?.code_set?.id,
        mode: direction === 'outbound' ? 'receiving' : 'sending',
        type: 'patients',
        date_range: { gte: widgetConfig?.minYear, lte: widgetConfig?.maxYear },
        filter_sets: [],
        filters: [],
      },
    };
  }, [
    volumeKey,
    direction,
    widgetConfig?.minYear,
    widgetConfig?.maxYear,
    widgetConfig?.widget?.code_set?.id,
  ]);

  const {
    data: paginatedData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetReferralsV2PaginatedInfiniteQuery(
    {
      providerId: String(providerId),
      queryParams,
      data: postBody,
    },
    { skip: !providerId }
  );

  const rows = useMemo(
    () => paginatedData?.pages?.flatMap((page: any) => page.results) ?? [],
    [paginatedData]
  );

  const { fetchTriggerRef } = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetching: isLoading || !!isFetchingNextPage,
    fetchNextPage,
  });

  const codeSetLabel = widgetConfig?.filterSet?.label
    ? `${widgetConfig.filterSet.label} Claims`
    : 'All Claims';

  const handleSelectAll = useCallback(() => {
    if (selectedProviders.size === rows.length) {
      setSelectedProviders(new Set());
    } else {
      setSelectedProviders(new Set(rows.map((r: any) => r.provider_id)));
    }
  }, [selectedProviders.size, rows]);

  const handleProviderToggle = useCallback((providerId: string) => {
    setSelectedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(providerId)) {
        next.delete(providerId);
      } else {
        next.add(providerId);
      }
      return next;
    });
  }, []);

  const selectedProvidersData = useMemo(
    () => rows.filter((r: any) => selectedProviders.has(r.provider_id)),
    [rows, selectedProviders]
  );

  const hasSelectedProviders = selectedProviders.size > 0;
  const allSelected = selectedProviders.size === rows.length && rows.length > 0;

  const handleRowClick = useCallback(
    (row: Record<string, any>) => {
      const rowType = row.type || 'hcp';
      const rowId = row.provider_id || row.id;
      router.push(getProfileUrl(rowType, rowId));
      closeDrawer('profile');
    },
    [router, closeDrawer]
  );

  const getPatientCount = (row: Record<string, any>) => {
    let value: number | null = null;
    if (volumeKey) {
      value = row.patients?.[volumeKey] != null ? Number(row.patients[volumeKey]) : null;
    } else {
      value = row.total_patients != null ? Number(row.total_patients) : null;
    }
    if (value == null) return '-';
    return applyCaptureRate(value, captureRate).toLocaleString();
  };

  const getClaimCount = (row: Record<string, any>) => {
    let value: number | null = null;
    if (volumeKey) {
      value = row.claims?.[volumeKey] != null ? Number(row.claims[volumeKey]) : null;
    } else {
      value = row.total_claims != null ? Number(row.total_claims) : null;
    }
    if (value == null) return '-';
    return applyCaptureRate(value, captureRate).toLocaleString();
  };

  return (
    <Card
      className="p-4 flex flex-col gap-4 bg-white h-full"
      data-testid={testIdPrefix}
    >
      <WidgetHeader
        fallbackTitle={`${title} - ${codeSetLabel}`}
        trailing={captureRate != null ? <CaptureRateBadge rate={captureRate} /> : undefined}
      />

      <div className="flex-1 min-h-0 max-h-80 overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:rounded-3xl">
        {isLoading || widgetConfig?.isLoading ? (
          <WidgetLoading data-testid={`${testIdPrefix}-loading`} />
        ) : rows.length === 0 ? (
          <WidgetEmpty data-testid={`${testIdPrefix}-empty`} variant="full" />
        ) : (
          <div
            className="flex flex-col"
            role="list"
            aria-label={title}
            data-testid={`${testIdPrefix}-list`}
          >
              <div className="flex items-center justify-between gap-2 pb-1 border-b border-neutral-100 sticky top-0 bg-white z-10">
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={allSelected}
                      onChange={handleSelectAll}
                      size="sm"
                      variant="solid"
                      color="primary"
                      data-testid={`${testIdPrefix}-select-all`}
                      aria-label="Select all providers"
                    />
                    <span className="text-xs font-medium text-primary leading-none">
                      Select all
                    </span>
                  </label>
                  <PulseButton
                    variant="plain"
                    size="xs"
                    onClick={() => setShowAddToListModal(true)}
                    disabled={!hasSelectedProviders}
                    className="whitespace-nowrap"
                    data-testid={`${testIdPrefix}-add-to-list-button`}
                  >
                    <Icon icon={faList} className="w-3 h-3" />
                    Add to List
                  </PulseButton>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-tertiary min-w-16 text-end">
                    Patients
                  </span>
                  <span className="text-xs font-semibold text-tertiary min-w-16 text-end">
                    Claims
                  </span>
                </div>
              </div>
              {rows.map((row: Record<string, any>) => (
                <div
                  key={row.provider_id}
                  className="flex items-center justify-between gap-2 py-3 border-b border-neutral-100 last:border-b-0 cursor-pointer hover:bg-neutral-50 transition-colors"
                  role="listitem"
                  onClick={() => handleRowClick(row)}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="flex-shrink-0"
                    >
                      <Checkbox
                        checked={selectedProviders.has(row.provider_id)}
                        onChange={() => handleProviderToggle(row.provider_id)}
                        size="sm"
                        variant="solid"
                        color="primary"
                        data-testid={`${testIdPrefix}-checkbox-${row.provider_id}`}
                        aria-label={`Select ${row.name}`}
                      />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-bold text-link truncate">
                        {row.name}
                      </span>
                      {row.specialty && (
                        <span className="text-xs text-secondary truncate">
                          {row.specialty}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <CompactCRMButton crmLinks={row.crm_links || []} />
                    <div className="flex gap-2">
                      <div className="flex items-center justify-end px-2 py-1 min-w-16">
                        <span className="text-sm font-semibold text-teal-900 tabular-nums">
                          {getPatientCount(row)}
                        </span>
                      </div>
                      <div className="flex items-center justify-end px-2 py-1 min-w-16">
                        <span className="text-sm font-semibold text-teal-900 tabular-nums">
                          {getClaimCount(row)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Infinite scroll sentinel + spinner */}
              <div
                ref={fetchTriggerRef}
                className="h-1"
                aria-hidden="true"
                data-testid={`${testIdPrefix}-scroll-trigger`}
              />
              {isFetchingNextPage && (
                <WidgetLoading />
              )}
          </div>
        )}
      </div>

      <AddToListModal
        isOpen={showAddToListModal}
        onClose={() => setShowAddToListModal(false)}
        mode="add"
        providers={selectedProvidersData}
        onSuccess={() => setSelectedProviders(new Set())}
      />
    </Card>
  );
};

export default CompactReferralsList;
